import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, useScroll, useTransform, useInView } from 'framer-motion';

// ============================================
// Service Accent Colors
// ============================================

export type ServiceAccent = 'emerald' | 'blue' | 'violet' | 'orange';

const accentColors: Record<ServiceAccent, {
  primary: string;
  secondary: string;
  glow: string;
  gradient: string;
  text: string;
  bg: string;
  border: string;
}> = {
  emerald: {
    primary: '#10b981',
    secondary: '#34d399',
    glow: 'rgba(16, 185, 129, 0.3)',
    gradient: 'from-emerald-400 to-teal-400',
    text: 'text-emerald-400',
    bg: 'bg-emerald-500',
    border: 'border-emerald-500/30',
  },
  blue: {
    primary: '#3b82f6',
    secondary: '#60a5fa',
    glow: 'rgba(59, 130, 246, 0.3)',
    gradient: 'from-blue-400 to-cyan-400',
    text: 'text-blue-400',
    bg: 'bg-blue-500',
    border: 'border-blue-500/30',
  },
  violet: {
    primary: '#8b5cf6',
    secondary: '#a78bfa',
    glow: 'rgba(139, 92, 246, 0.3)',
    gradient: 'from-violet-400 to-purple-400',
    text: 'text-violet-400',
    bg: 'bg-violet-500',
    border: 'border-violet-500/30',
  },
  orange: {
    primary: '#f97316',
    secondary: '#fb923c',
    glow: 'rgba(249, 115, 22, 0.3)',
    gradient: 'from-orange-400 to-amber-400',
    text: 'text-orange-400',
    bg: 'bg-orange-500',
    border: 'border-orange-500/30',
  },
};

// ============================================
// Animated Service Hero
// ============================================

interface ServiceHeroProps {
  eyebrow?: string;
  title: string;
  subtitle: string;
  description: string;
  ctaText: string;
  ctaHref: string;
  metrics?: Array<{ value: string; label: string }>;
  accent?: ServiceAccent;
  icon?: React.ReactNode;
}

export const ServiceHero: React.FC<ServiceHeroProps> = ({
  eyebrow,
  title,
  subtitle,
  description,
  ctaText,
  ctaHref,
  metrics = [],
  accent = 'emerald',
  icon,
}) => {
  const colors = accentColors[accent];
  const heroRef = useRef<HTMLElement>(null);
  const { scrollY } = useScroll();
  const opacity = useTransform(scrollY, [0, 400], [1, 0]);
  const y = useTransform(scrollY, [0, 400], [0, 100]);

  return (
    <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        {/* Gradient mesh */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full blur-[150px]"
          style={{ background: colors.glow }}
          animate={{
            scale: [1, 1.2, 1],
            x: [0, 50, 0],
            y: [0, 30, 0],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full blur-[120px] opacity-50"
          style={{ background: `rgba(139, 92, 246, 0.2)` }}
          animate={{
            scale: [1, 1.3, 1],
            x: [0, -30, 0],
            y: [0, -50, 0],
          }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
        />
      </div>

      {/* Animated Icon */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        style={{ opacity, y }}
      >
        <div className="relative w-[500px] h-[500px]">
          {/* Rotating rings */}
          <motion.div
            className="absolute inset-0 rounded-full border opacity-20"
            style={{ borderColor: colors.primary }}
            animate={{ rotate: 360 }}
            transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
          />
          <motion.div
            className="absolute inset-8 rounded-full border border-dashed opacity-15"
            style={{ borderColor: colors.secondary }}
            animate={{ rotate: -360 }}
            transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
          />
          <motion.div
            className="absolute inset-16 rounded-full border opacity-10"
            style={{ borderColor: colors.primary }}
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          />

          {/* Floating particles */}
          {[...Array(12)].map((_, i) => {
            const angle = (i / 12) * Math.PI * 2;
            const radius = 180 + (i % 3) * 30;
            return (
              <motion.div
                key={i}
                className="absolute w-2 h-2 rounded-full"
                style={{
                  left: '50%',
                  top: '50%',
                  backgroundColor: colors.primary,
                }}
                animate={{
                  x: [
                    Math.cos(angle) * radius,
                    Math.cos(angle + 0.5) * (radius + 20),
                    Math.cos(angle) * radius,
                  ],
                  y: [
                    Math.sin(angle) * radius,
                    Math.sin(angle + 0.5) * (radius + 20),
                    Math.sin(angle) * radius,
                  ],
                  opacity: [0.3, 0.7, 0.3],
                  scale: [0.8, 1.2, 0.8],
                }}
                transition={{
                  duration: 5 + i * 0.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: i * 0.2,
                }}
              />
            );
          })}

          {/* Center icon */}
          {icon && (
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              animate={{
                scale: [1, 1.05, 1],
              }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            >
              <div
                className="w-24 h-24 rounded-2xl flex items-center justify-center"
                style={{
                  background: `linear-gradient(135deg, ${colors.primary}20, ${colors.secondary}10)`,
                  border: `1px solid ${colors.primary}30`,
                }}
              >
                {icon}
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
        {eyebrow && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-sm text-zinc-400 mb-4 max-w-2xl mx-auto"
          >
            {eyebrow}
          </motion.p>
        )}

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: eyebrow ? 0.1 : 0 }}
          className={`text-sm font-medium ${colors.text} mb-4 tracking-wide uppercase`}
        >
          {subtitle}
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 leading-tight"
        >
          {title}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-lg sm:text-xl text-zinc-400 mb-8 max-w-2xl mx-auto"
        >
          {description}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <motion.a
            href={ctaHref}
            className={`inline-flex items-center gap-2 px-8 py-4 ${colors.bg} hover:opacity-90 text-white font-semibold rounded-xl transition-all shadow-lg`}
            style={{ boxShadow: `0 10px 40px -10px ${colors.glow}` }}
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
          >
            {ctaText}
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </motion.a>
        </motion.div>

        {/* Metrics */}
        {metrics.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-wrap justify-center gap-8 mt-16"
          >
            {metrics.map((metric, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="text-center"
              >
                <p className={`text-3xl sm:text-4xl font-bold bg-gradient-to-r ${colors.gradient} text-transparent bg-clip-text`}>
                  {metric.value}
                </p>
                <p className="text-zinc-500 text-sm mt-1">{metric.label}</p>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-6 h-10 rounded-full border-2 border-zinc-700 flex justify-center pt-2">
          <motion.div
            className="w-1.5 h-1.5 rounded-full bg-zinc-500"
            animate={{ y: [0, 12, 0], opacity: [1, 0.3, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </div>
      </motion.div>
    </section>
  );
};

// ============================================
// Animated Breadcrumb
// ============================================

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface AnimatedBreadcrumbProps {
  items: BreadcrumbItem[];
  accent?: ServiceAccent;
}

export const AnimatedBreadcrumb: React.FC<AnimatedBreadcrumbProps> = ({
  items,
  accent = 'emerald',
}) => {
  const colors = accentColors[accent];

  return (
    <nav className="py-6 px-6 lg:px-8">
      <div className="max-w-7xl mx-auto flex items-center flex-wrap gap-2">
        {items.map((item, index) => (
          <div key={index} className="flex items-center">
            {index > 0 && (
              <motion.div
                initial={{ scale: 0, rotate: -90 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: index * 0.1, type: 'spring', stiffness: 300 }}
                className="mx-3"
              >
                <motion.svg
                  className="w-4 h-4 text-zinc-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  animate={{ x: [0, 3, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity, delay: index * 0.2 }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </motion.svg>
              </motion.div>
            )}

            <motion.span
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 + 0.1 }}
            >
              {item.href && index < items.length - 1 ? (
                <a
                  href={item.href}
                  className="text-sm text-zinc-400 hover:text-white transition-colors relative group"
                >
                  {item.label}
                  <span
                    className="absolute bottom-0 left-0 w-0 h-px group-hover:w-full transition-all duration-300"
                    style={{ backgroundColor: colors.primary }}
                  />
                </a>
              ) : (
                <span className={`text-sm font-medium ${index === items.length - 1 ? colors.text : 'text-zinc-400'}`}>
                  {item.label}
                </span>
              )}
            </motion.span>
          </div>
        ))}
      </div>
    </nav>
  );
};

// ============================================
// Horizontal Process Stepper
// ============================================

interface ProcessStep {
  title: string;
  description: string;
  duration?: string;
}

interface ProcessStepperProps {
  steps: ProcessStep[];
  accent?: ServiceAccent;
  autoAdvance?: boolean;
  autoAdvanceInterval?: number;
}

export const ProcessStepper: React.FC<ProcessStepperProps> = ({
  steps,
  accent = 'emerald',
  autoAdvance = true,
  autoAdvanceInterval = 4000,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isInView, setIsInView] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inView = useInView(containerRef, { once: false, margin: '-100px' });
  const colors = accentColors[accent];

  useEffect(() => {
    setIsInView(inView);
  }, [inView]);

  useEffect(() => {
    if (!autoAdvance || !isInView) return;

    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, autoAdvanceInterval);

    return () => clearInterval(interval);
  }, [autoAdvance, autoAdvanceInterval, steps.length, isInView]);

  return (
    <div ref={containerRef} className="py-20">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Steps Navigation */}
        <div className="relative mb-16">
          {/* Connecting line */}
          <div className="absolute top-6 left-0 right-0 h-[2px] bg-zinc-800">
            <motion.div
              className="h-full"
              style={{ backgroundColor: colors.primary }}
              initial={{ width: '0%' }}
              animate={{ width: `${((activeStep + 1) / steps.length) * 100}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </div>

          {/* Step indicators */}
          <div className="relative flex justify-between">
            {steps.map((step, index) => (
              <motion.button
                key={index}
                onClick={() => setActiveStep(index)}
                className="flex flex-col items-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                {/* Circle */}
                <motion.div
                  className={`
                    relative w-12 h-12 rounded-full flex items-center justify-center
                    border-2 transition-colors z-10
                    ${index <= activeStep ? `${colors.bg} border-transparent` : 'bg-zinc-900 border-zinc-700'}
                  `}
                  animate={index === activeStep ? {
                    scale: [1, 1.1, 1],
                    boxShadow: [
                      `0 0 0 0 ${colors.glow}`,
                      `0 0 20px 5px ${colors.glow}`,
                      `0 0 0 0 ${colors.glow}`,
                    ],
                  } : {}}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <span className={`text-sm font-bold ${index <= activeStep ? 'text-white' : 'text-zinc-500'}`}>
                    {String(index + 1).padStart(2, '0')}
                  </span>

                  {/* Animated ring for active */}
                  {index === activeStep && (
                    <motion.div
                      className="absolute inset-0 rounded-full border-2"
                      style={{ borderColor: colors.primary }}
                      initial={{ scale: 1, opacity: 1 }}
                      animate={{ scale: 1.5, opacity: 0 }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    />
                  )}
                </motion.div>

                {/* Label */}
                <motion.p
                  className={`mt-3 text-sm font-medium transition-colors ${
                    index === activeStep ? 'text-white' : 'text-zinc-500'
                  }`}
                >
                  {step.duration || `Step ${index + 1}`}
                </motion.p>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeStep}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="text-center max-w-2xl mx-auto"
          >
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              {steps[activeStep].title}
            </h3>
            <p className="text-zinc-400 text-lg leading-relaxed">
              {steps[activeStep].description}
            </p>
          </motion.div>
        </AnimatePresence>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 mt-8">
          {steps.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveStep(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === activeStep ? `w-8 ${colors.bg}` : 'bg-zinc-700'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================
// Feature Row with Large Numbers
// ============================================

interface FeatureRowProps {
  number: string;
  title: string;
  description: string;
  features: string[];
  imagePosition?: 'left' | 'right';
  accent?: ServiceAccent;
  image?: React.ReactNode;
}

export const FeatureRow: React.FC<FeatureRowProps> = ({
  number,
  title,
  description,
  features,
  imagePosition = 'right',
  accent = 'emerald',
  image,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const colors = accentColors[accent];

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : {}}
      transition={{ duration: 0.6 }}
      className={`flex flex-col ${imagePosition === 'left' ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-12 lg:gap-20 py-20`}
    >
      {/* Content */}
      <div className="flex-1 relative">
        {/* Large faded number */}
        <motion.span
          initial={{ opacity: 0, x: imagePosition === 'left' ? 50 : -50 }}
          animate={isInView ? { opacity: 0.05, x: 0 } : {}}
          transition={{ duration: 0.8 }}
          className={`absolute -top-16 ${imagePosition === 'left' ? '-right-8' : '-left-8'} text-[200px] font-bold text-white pointer-events-none select-none`}
        >
          {number}
        </motion.span>

        <div className="relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <span className={`inline-block px-3 py-1 text-xs font-semibold ${colors.text} ${colors.border} border rounded-full mb-4`}>
              {number}
            </span>
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              {title}
            </h3>
            <p className="text-zinc-400 text-lg mb-6 leading-relaxed">
              {description}
            </p>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-3"
          >
            {features.map((feature, index) => (
              <motion.li
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                className="flex items-start gap-3"
              >
                <svg
                  className={`w-5 h-5 mt-0.5 flex-shrink-0 ${colors.text}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-zinc-300">{feature}</span>
              </motion.li>
            ))}
          </motion.ul>
        </div>
      </div>

      {/* Image/Visual */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={isInView ? { opacity: 1, scale: 1 } : {}}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="flex-1 w-full"
      >
        {image || (
          <div
            className="aspect-square rounded-2xl"
            style={{
              background: `linear-gradient(135deg, ${colors.glow}, rgba(139, 92, 246, 0.1))`,
            }}
          />
        )}
      </motion.div>
    </motion.div>
  );
};

// ============================================
// Use Cases Accordion
// ============================================

interface UseCase {
  title: string;
  description: string;
  result?: string;
}

interface UseCasesAccordionProps {
  title: string;
  cases: UseCase[];
  accent?: ServiceAccent;
}

export const UseCasesAccordion: React.FC<UseCasesAccordionProps> = ({
  title,
  cases,
  accent = 'emerald',
}) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const colors = accentColors[accent];

  return (
    <div className="py-20">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-3xl sm:text-4xl font-bold text-white text-center mb-12"
        >
          {title}
        </motion.h2>

        <div className="space-y-4">
          {cases.map((useCase, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="border border-zinc-800 rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-zinc-800/30 transition-colors"
              >
                <span className="flex items-center gap-4">
                  <span
                    className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                      openIndex === index ? `${colors.bg} text-white` : 'bg-zinc-800 text-zinc-400'
                    }`}
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="text-lg font-semibold text-white">{useCase.title}</span>
                </span>
                <motion.svg
                  className="w-5 h-5 text-zinc-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  animate={{ rotate: openIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </motion.svg>
              </button>

              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                  >
                    <div className="px-6 pb-6 pl-[4.5rem]">
                      <p className="text-zinc-400 mb-4">{useCase.description}</p>
                      {useCase.result && (
                        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${colors.bg}/10 ${colors.border} border`}>
                          <svg className={`w-4 h-4 ${colors.text}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                          </svg>
                          <span className={`text-sm font-medium ${colors.text}`}>{useCase.result}</span>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================
// Tech Stack Grid
// ============================================

interface TechItem {
  name: string;
  icon?: React.ReactNode;
  category?: string;
}

interface TechStackGridProps {
  title: string;
  items: TechItem[];
  accent?: ServiceAccent;
}

export const TechStackGrid: React.FC<TechStackGridProps> = ({
  title,
  items,
  accent = 'emerald',
}) => {
  const colors = accentColors[accent];

  return (
    <div className="py-20 bg-zinc-900/30">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-3xl sm:text-4xl font-bold text-white text-center mb-4"
        >
          {title}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-zinc-400 text-center mb-12"
        >
          Technologies and tools we leverage for maximum impact
        </motion.p>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {items.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              whileHover={{
                scale: 1.05,
                boxShadow: `0 0 30px ${colors.glow}`,
              }}
              className="group bg-zinc-800/50 border border-zinc-700/50 rounded-xl p-6 flex flex-col items-center justify-center gap-3 cursor-pointer transition-colors hover:border-zinc-600"
            >
              <div className={`w-12 h-12 rounded-xl bg-zinc-700/50 flex items-center justify-center text-zinc-400 group-hover:${colors.text} transition-colors`}>
                {item.icon || (
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                )}
              </div>
              <span className="text-sm font-medium text-zinc-300 text-center">{item.name}</span>
              {item.category && (
                <span className="text-xs text-zinc-500">{item.category}</span>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================
// Related Services Carousel
// ============================================

interface RelatedService {
  title: string;
  description: string;
  href: string;
  accent: ServiceAccent;
}

interface RelatedServicesCarouselProps {
  title: string;
  services: RelatedService[];
}

export const RelatedServicesCarousel: React.FC<RelatedServicesCarouselProps> = ({
  title,
  services,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const scroll = useCallback((direction: 'left' | 'right') => {
    if (!containerRef.current) return;
    const scrollAmount = containerRef.current.offsetWidth * 0.8;
    containerRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  }, []);

  return (
    <div className="py-20 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between mb-12">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl sm:text-4xl font-bold text-white"
          >
            {title}
          </motion.h2>

          <div className="flex gap-2">
            <button
              onClick={() => scroll('left')}
              className="w-10 h-10 rounded-full border border-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white hover:border-zinc-500 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => scroll('right')}
              className="w-10 h-10 rounded-full border border-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white hover:border-zinc-500 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        <div
          ref={containerRef}
          className="flex gap-6 overflow-x-auto scrollbar-hide pb-4 -mx-6 px-6 snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {services.map((service, index) => {
            const colors = accentColors[service.accent];
            return (
              <motion.a
                key={index}
                href={service.href}
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                className="flex-shrink-0 w-80 snap-start"
              >
                <div
                  className="h-full p-6 rounded-2xl border border-zinc-800 bg-zinc-900/50 hover:border-zinc-700 transition-all"
                  style={{
                    boxShadow: `0 0 0 0 ${colors.glow}`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = `0 20px 40px -15px ${colors.glow}`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = `0 0 0 0 ${colors.glow}`;
                  }}
                >
                  <div className={`w-12 h-12 rounded-xl ${colors.bg}/20 flex items-center justify-center mb-4`}>
                    <svg className={`w-6 h-6 ${colors.text}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{service.title}</h3>
                  <p className="text-zinc-400 text-sm mb-4">{service.description}</p>
                  <span className={`inline-flex items-center gap-1 text-sm font-medium ${colors.text}`}>
                    Learn more
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </motion.a>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ============================================
// Sticky Book Now Bar
// ============================================

interface StickyBookBarProps {
  ctaText: string;
  ctaHref: string;
  price?: string;
  guarantee?: string;
  accent?: ServiceAccent;
}

export const StickyBookBar: React.FC<StickyBookBarProps> = ({
  ctaText,
  ctaHref,
  price,
  guarantee,
  accent = 'emerald',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const colors = accentColors[accent];

  useEffect(() => {
    const handleScroll = () => {
      // Show after scrolling past hero (approximately 100vh)
      setIsVisible(window.scrollY > window.innerHeight);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="fixed bottom-0 left-0 right-0 z-50 bg-zinc-900/95 backdrop-blur-xl border-t border-zinc-800"
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              {price && (
                <div className="text-center sm:text-left">
                  <p className="text-2xl font-bold text-white">{price}</p>
                  <p className="text-xs text-zinc-500">One-time investment</p>
                </div>
              )}
              {guarantee && (
                <div className="hidden md:flex items-center gap-2 text-sm">
                  <svg className={`w-5 h-5 ${colors.text}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span className="text-zinc-400">{guarantee}</span>
                </div>
              )}
            </div>

            <motion.a
              href={ctaHref}
              className={`px-8 py-3 ${colors.bg} text-white font-semibold rounded-xl shadow-lg transition-all`}
              style={{ boxShadow: `0 10px 30px -10px ${colors.glow}` }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {ctaText}
            </motion.a>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ServiceHero;
